import React from 'react'
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Divider,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface BreadcrumbItem {
  label: string
  path?: string
}

interface PageAction {
  label: string
  onClick: () => void
  variant?: 'contained' | 'outlined' | 'text'
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  icon?: React.ReactElement
  disabled?: boolean
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  breadcrumbs?: BreadcrumbItem[]
  actions?: PageAction[]
  children?: React.ReactNode
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions = [],
  children,
}) => {
  const navigate = useNavigate()

  const handleBreadcrumbClick = (path: string) => {
    navigate(path)
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 1 }}
        >
          {breadcrumbs.map((item, index) => (
            item.path ? (
              <Link
                key={index}
                color="inherit"
                onClick={() => handleBreadcrumbClick(item.path!)}
                sx={{ 
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography key={index} color="text.primary">
                {item.label}
              </Typography>
            )
          ))}
        </Breadcrumbs>
      )}

      {/* Header Content */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
      }}>
        {/* Title and Subtitle */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={600}
            sx={{ mb: subtitle ? 0.5 : 0, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        {actions.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'contained'}
                color={action.color || 'secondary'}
                onClick={action.onClick}
                startIcon={action.icon}
                disabled={action.disabled}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Custom Content */}
      {children && (
        <Box sx={{ mt: 2 }}>
          {children}
        </Box>
      )}

      <Divider sx={{ mt: 2 }} />
    </Box>
  )
}

export default PageHeader